/* =============================================================================
 * Estado da sessao.
 *
 * Este modulo e' DELIBERADAMENTE burro: guarda estado e nada mais. Ele nao
 * chama a API. Quem chama e' sessao/autenticacao.js e api/token.js - e e' isso
 * que evita o ciclo de import (ServicoAplicacao.js precisa ler o token daqui).
 * ========================================================================== */

import { persistente, daSessao, limparTudo } from '@shared/lib/armazenamento.js';
import { criptografarBase64, descriptografarBase64 } from '@shared/lib/cripto.js';
import { normalizarUrl } from '@shared/lib/config.js';
import { paraData, paraInteiro, paraLista, paraTexto } from '@shared/lib/valores.js';
import { emitir, EVENTOS } from '@shared/lib/eventos.js';

//Chave de ofuscacao da credencial guardada. Ver o aviso em guardarCredencial().
const CHAVE_CREDENCIAL = 'p';

export const sessao = {
    usuario: null,
    token: null,
    tokenExpiracao: null,
    //Diferenca entre o relogio do servidor e o do dispositivo. Sem isso, um
    //celular com a hora errada acha que o token ja' expirou (ou que nunca expira).
    deltaHoraServidor: 0,

    perfis: [],
    restricoes: [],
    atletasVinculados: [],
    //Vem de TAB_USUARIO_CLUBE, nao dos perfis: e' o unico lugar que diz DE QUAL
    //clube a pessoa e' dirigente.
    clubesVinculados: [],
    //Seletor de tenant. Vem do SERVIDOR no boot e entra no hash da URL de
    //imagem - sem ele, todo escudo do app quebra.
    identificadorConexao: '',
    configGeral: null,
    /**
     * Por onde os ARQUIVOS deste ambiente sao servidos -
     * `ConfigGeral.UrlPrismaFile`, resolvido pelo servidor a cada shell (ver
     * LibPrisma.ObtemUrlPrismaFile no backend).
     *
     * FICA FORA DE `configGeral` de proposito, num campo so' dele: e' o unico
     * pedaco do config que a casca usa em toda tela (todo escudo, todo retrato,
     * todo documento passa por ele), e o unico que precisa sobreviver ao
     * recarregamento - ver `restaurar()`.
     *
     * Vazio significa "use a queda", e nao "ainda nao sei": o servidor pode nao
     * ter um servico de arquivos proprio. Quem monta a URL e' lib/arquivos.js.
     */
    urlServicoArquivo: '',

    get autenticado() {
        return !!this.usuario;
    },
};

export function restaurar() {
    sessao.usuario = persistente.ler('usuario');
    sessao.token = persistente.ler('token');
    sessao.tokenExpiracao = persistente.ler('tokenExpiracao');
    sessao.deltaHoraServidor = paraInteiro(persistente.ler('deltaHoraServidor', 0));
    //PERSISTENTE, e nao mais so' da sessao do navegador. O tenant entra no
    //contexto de tudo o que o modo offline grava (ver shared/offline/offline.js):
    //num aparelho que abriu o app sem rede, o valor da sessao anterior ja' morreu
    //e o contexto sairia diferente do que gravou o pacote - ele existiria na base
    //e nunca seria encontrado, sem erro nenhum no console.
    //A leitura tolera o valor antigo, da sessao, para quem ja' tinha o app aberto.
    sessao.identificadorConexao = persistente.ler('identificadorConexao', '') || daSessao.ler('identificadorConexao', '') || '';
    //PELO MESMO MOTIVO DO TENANT: a URL do arquivo entra no endereco de toda
    //imagem, e o service worker guarda o que baixou POR ENDERECO. Um boot sem
    //rede que caisse na queda `<UrlServico>/file` pediria imagens com um endereco
    //diferente do que foi cacheado - nenhuma delas apareceria, e sem erro nenhum.
    sessao.urlServicoArquivo = persistente.ler('urlServicoArquivo', '') || '';
}

/** Aplica o retorno de AutentificarUsuario. */
export function aplicarAutenticacao(resultado, { manterConectado = false, senha = null } = {}) {
    sessao.usuario = resultado.Usuario ?? null;
    sessao.token = resultado.TokenAutenticacao ?? null;
    sessao.tokenExpiracao = resultado.TokenExpiracao ?? null;

    const horaServidor = paraData(resultado.HoraServidor);
    sessao.deltaHoraServidor = horaServidor ? horaServidor.getTime() - Date.now() : 0;

    persistente.gravar('usuario', sessao.usuario);
    persistente.gravar('token', sessao.token);
    persistente.gravar('tokenExpiracao', sessao.tokenExpiracao);
    persistente.gravar('deltaHoraServidor', sessao.deltaHoraServidor);

    if (senha && sessao.usuario) {
        guardarCredencial({ login: sessao.usuario.Login, senha }, manterConectado);
    }

    emitir(EVENTOS.SESSAO_ALTERADA, sessao);
}

/** Aplica o retorno de ObterInformacoesShellUsuario(Anonimo). */
export function aplicarShell(resultado) {
    sessao.identificadorConexao = resultado?.Solicitacao?.IdentificadorConexao || sessao.identificadorConexao || '';
    sessao.perfis = paraLista(resultado?.Perfis);
    sessao.restricoes = paraLista(resultado?.Restricoes);
    sessao.configGeral = resultado?.ConfigGeral ?? null;
    if (resultado?.Usuario) sessao.usuario = resultado.Usuario;

    //A RESPOSTA DO SHELL MANDA, inclusive quando ela e' vazia: o ambiente pode
    //deixar de ter servico de arquivos proprio, e o valor guardado da vez passada
    //continuaria apontando para um endereco que ninguem mais atende. So' um shell
    //que NAO trouxe ConfigGeral (erro, resposta parcial) preserva o que havia.
    if (resultado?.ConfigGeral) {
        sessao.urlServicoArquivo = normalizarUrl(resultado.ConfigGeral.UrlPrismaFile);
        persistente.gravar('urlServicoArquivo', sessao.urlServicoArquivo);
    }

    persistente.gravar('identificadorConexao', sessao.identificadorConexao);
    emitir(EVENTOS.SESSAO_ALTERADA, sessao);
}

/**
 * A foto do usuario logado mudou (ver autenticacao.js#trocarMinhaFoto).
 *
 * GRAVA JUNTO, e nao so' em memoria: o retrato aparece no primeiro paint da
 * casca, antes de o boot terminar de falar com o servidor. Sem persistir, quem
 * trocasse a foto e recarregasse veria a anterior voltar por um instante.
 *
 * Objeto novo em vez de mutacao no lugar: o usuario tambem esta' guardado no
 * armazenamento, e trocar a referencia deixa claro que quem quiser a foto nova
 * tem que reler `sessao.usuario` - que e' justamente o que o evento avisa.
 */
export function definirImagemUsuario(caminho) {
    if (!sessao.usuario) return;

    sessao.usuario = { ...sessao.usuario, Imagem: paraTexto(caminho) };
    persistente.gravar('usuario', sessao.usuario);
    emitir(EVENTOS.SESSAO_ALTERADA, sessao);
}

export function definirAtletasVinculados(atletas) {
    sessao.atletasVinculados = paraLista(atletas);
    emitir(EVENTOS.SESSAO_ALTERADA, sessao);
}

export function definirClubesVinculados(clubes) {
    sessao.clubesVinculados = paraLista(clubes);
    emitir(EVENTOS.SESSAO_ALTERADA, sessao);
}

/* -----------------------------------------------------------------------------
 * Credencial guardada
 *
 * DIVIDA TECNICA CONHECIDA. O backend nao tem refresh token: a unica forma de
 * renovar a sessao e' reautenticar com login e senha. Por isso a senha fica
 * guardada, ofuscada - e ofuscacao XOR nao e' criptografia, e' reversivel em
 * tres linhas no console.
 *
 * Mitigacoes: so' vai para o localStorage se o usuario marcar "manter
 * conectado" (senao morre ao fechar a aba), e todo o acesso passa por estas
 * duas funcoes - quando o endpoint RenovarTokenAutenticacao existir, apaga-se
 * daqui e nada mais no app precisa mudar.
 * -------------------------------------------------------------------------- */

function guardarCredencial({ login, senha }, manterConectado) {
    const area = manterConectado ? persistente : daSessao;
    const outra = manterConectado ? daSessao : persistente;
    outra.remover('credencial');
    area.gravar('credencial', { login, senha: criptografarBase64(senha, CHAVE_CREDENCIAL) });
    persistente.gravar('manterConectado', manterConectado);
}

export function credencialGuardada() {
    const guardada = persistente.ler('credencial') ?? daSessao.ler('credencial');
    if (!guardada?.login || !guardada?.senha) return null;
    try {
        return { login: guardada.login, senha: descriptografarBase64(guardada.senha, CHAVE_CREDENCIAL) };
    } catch {
        return null;
    }
}

export function encerrar({ motivo = 'logout' } = {}) {
    const identificadorConexao = sessao.identificadorConexao;
    const urlServicoArquivo = sessao.urlServicoArquivo;

    sessao.usuario = null;
    sessao.token = null;
    sessao.tokenExpiracao = null;
    sessao.deltaHoraServidor = 0;
    sessao.perfis = [];
    sessao.restricoes = [];
    sessao.atletasVinculados = [];
    //Faltava: sem limpar, `capacidades()` continuava devolvendo ehDirigente e
    //podeGerirClube(id) do usuario ANTERIOR ate' o proximo recarregamento -
    //limparTudo() so' alcanca o storage, e este vinculo vive em memoria.
    sessao.clubesVinculados = [];

    limparTudo();

    //O tenant nao e' do usuario - preservar evita um round-trip extra no boot, e
    //e' o que mantem o contexto do offline estavel entre uma conta e outra. O
    //endereco do servico de arquivos e' do AMBIENTE pelo mesmo motivo: sair da
    //conta nao muda por onde os escudos sao servidos, e sem ele a tela de login
    //perderia as imagens ate' o proximo shell responder.
    sessao.identificadorConexao = identificadorConexao;
    persistente.gravar('identificadorConexao', identificadorConexao);
    sessao.urlServicoArquivo = urlServicoArquivo;
    persistente.gravar('urlServicoArquivo', urlServicoArquivo);

    emitir(EVENTOS.SESSAO_ENCERRADA, { motivo });
    emitir(EVENTOS.SESSAO_ALTERADA, sessao);
}
